import React, {
    useState,
    useMemo,
    useCallback,
    useEffect,
    useContext,
} from "react";
import TopLabelInput from "./TopLabelInput";
import { FaCheck } from "react-icons/fa";
import FileTypeSelect from "./input-elements/FileTypeSelect";
import HardwareSelect from "./input-elements/HardwareSelect";
import PackageSelect from "./input-elements/PackageSelect";
import CustomerFacing from "./input-elements/CustomerFacing";
import EquipmentSelect from "./input-elements/EquipmentSelect";
import Loader from "../../../loader/loader";
import EquipmentImageWithLoader from "./EquipmentImageWithLoader";
import ApplyTPP from "./input-elements/ApplyTPP";
import { EquipmentContext } from "../useContext/context";
import VirtualTerminal from "./input-elements/VirtaulTerminal";
import CurrencyInput from "./input-elements/CurrencyInput";
import { makeStyles } from "@mui/styles";
import PINDebit from "./input-elements/PINDebit";
import CableLength from "./input-elements/CableLength";
import PercentageInput from "./input-elements/PercentageInput";
import AddSIMCard from "./input-elements/AddSIMCard";
import Connectivity from "./input-elements/Connectivity";
import PinPadInput from "./input-elements/PinPadInput";
import SimProvidedBy from "./input-elements/SimProvidedBy";
import AddonSelect from "./input-elements/AddonSelect";
import config from "../../../../config";

const useStyles = makeStyles({
    root: {
        marginTop: "2rem",
        "& .MuiFormControl-root": {
            marginTop: "1.5rem",
        },
    },
});

const PAYCHEX_ORG_ID = config.paymentcloud.PAYCHEX_ORG_ID;
const EMS_ORG_ID = config.paymentcloud.EMS_ORG_ID;

const Equipment = ({
    equipment,
    handleInputChange,
    handleTypeCheck,
    renderQuantity,
    renderActionBtn,
    onDeselectEquipment,
}) => {
    const { allEquipments, tpp_equipments_selected = [], org_id } = useContext(EquipmentContext);
    const classes = useStyles();
    const [isValorEquipment, setIsValorEquipment] = useState(false);

    const equipment_default = useMemo(() => {
        const {
            sass_fee_default: saas_fee,
            purchase_for_default: purchase_for,
            lease_for_default: lease_for,
            rent_for_default: rent_for,
            monthly_fee_default: monthly_fee,
            per_transaction_default: per_transaction,
            amount_default: amount,
            factor_rate_default: factor_rate,
            payback_default: payback,
            monthly_wireless_default: monthly_wireless,
            text_sms_fee_default: text_sms_fee,
            sim_card_monthly_default: sim_card_monthly,
        } = equipment;
        return {
            saas_fee,
            purchase_for,
            lease_for,
            rent_for,
            monthly_fee,
            per_transaction,
            amount,
            factor_rate,
            payback,
            monthly_wireless,
            text_sms_fee,
            sim_card_monthly,
        };
    }, [equipment]);

    useEffect(() => {
        setIsValorEquipment( (equipment.equipment_slug.includes("valor") || equipment?.brand_id === 1 ) );
    },[equipment.id]);

     // Watch for package_key changes and update sass_fee_default accordingly
     useEffect(() => {
        if (equipment.package_key && equipment.valor_dynamic_saas_fee_default) {
            const dynamicFee = equipment.valor_dynamic_saas_fee_default[equipment.package_key];
            if (dynamicFee) {
                handleInputChange('sass_fee_default', dynamicFee);
            }
        }
    }, [equipment.package_key]);

    const handleAddonChange = (selectedAddons) => {
        const dynamicFee = equipment.dynamic_monthly_fee_default?.[selectedAddons[0]?.value];
        if (dynamicFee) {
            handleInputChange('monthly_fee_default', dynamicFee);
        }
        handleInputChange('selected_addons', selectedAddons);
    };

    const onDeselect = () => {
        if (typeof onDeselectEquipment === "function") {
            onDeselectEquipment();
        }
    };

    const onInputChange = (e) => {
        try {
            e.persist();
        } catch (error) {}
        handleInputChange(e.target.name, e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onDeselect();
        }
    };

    return (
        <div className="eqp-col popular-eqp-col">
            <div className="equipment-item">
                {!!equipment.promotion_text && (
                    <div className="popular-tag">
                        <p>{equipment.promotion_text}</p>
                    </div>
                )}
                <div
                    className="eqp-img"
                    style={{ backgroundColor: "#E4F1FC" }}
                    onClick={onDeselect}
                    tabIndex="0"
                    role="button"
                    onKeyDown={handleKeyDown}
                >
                    <div className="eqp-img-overlay">
                        <div className="eqp-selected">
                            <FaCheck />
                        </div>
                    </div>
                    <EquipmentImageWithLoader id={equipment.id} />
                </div>
                <p className="eqp-name">{equipment.equipment_name}</p>
                {typeof renderQuantity === "function" &&
                    (org_id != PAYCHEX_ORG_ID ||
                        equipment.type != "apply_tpp") &&
                    renderQuantity()}
                {!!equipment && (
                    <div className={classes.root}>
                        {/* Render add-ons first if equipment ID is 19 and org is EMS */}
                        {equipment.id === 19 && org_id === EMS_ORG_ID && (
                            <>
                                {!!equipment.add_on_equipment && (
                                    <AddonSelect
                                        addon_list={equipment.add_on_equipment_list}
                                        handleOnChange={handleAddonChange}
                                        selectedAddons={equipment.selected_addons || []}
                                        id={equipment.id}
                                    />
                                )}
                            </>
                        )}
                        {/* Render all other fields */}
                        <>
                            {!equipment?.type && (
                                <span className="error-message">
                                     {!!equipment?.package && !equipment?.package_key ? 'Please select package first' : 'Please choose One'} 
                                </span>
                            )}
                            {!!equipment.sass_fee && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="SaaS Fee"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.saas_fee} / mo`}
                                    name={"sass_fee_default"}
                                    value={equipment.sass_fee_default}
                                    type="number"
                                    isChecked={equipment.type == "saas_fee"}
                                    onTypeCheck={handleTypeCheck}
                                    purchase_type={"saas_fee"}
                                    selectedType={equipment?.type}
                                />
                            )}
                            {!!equipment.purchase_for && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Purchase for"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.purchase_for}`}
                                    name={"purchase_for_default"}
                                    value={equipment.purchase_for_default}
                                    type="number"
                                    isChecked={equipment.type == "purchase_for"}
                                    onTypeCheck={handleTypeCheck}
                                    purchase_type={"purchase_for"}
                                    addTypeCheck
                                    selectedType={equipment?.type}
                                />
                            )}
                            {!!equipment.lease_for && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Lease for"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.lease_for} / mo`}
                                    name={"lease_for_default"}
                                    value={equipment.lease_for_default}
                                    type="number"
                                    isChecked={equipment.type == "lease_for"}
                                    onTypeCheck={handleTypeCheck}
                                    purchase_type={"lease_for"}
                                    addTypeCheck
                                    selectedType={equipment?.type}
                                />
                            )}
                            {!!equipment.rent_for && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Rent for"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.rent_for} / mo`}
                                    name={"rent_for_default"}
                                    value={equipment.rent_for_default}
                                    type="number"
                                    isChecked={equipment.type == "rent_for"}
                                    onTypeCheck={handleTypeCheck}
                                    purchase_type={"rent_for"}
                                    addTypeCheck
                                    selectedType={equipment?.type}
                                />
                            )}
                            {!!equipment.monthly_fee && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Monthly fee"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.monthly_fee}`}
                                    name={"monthly_fee_default"}
                                    value={equipment.monthly_fee_default}
                                    type="number"
                                    isChecked={equipment.type == "monthly_fee"}
                                    onTypeCheck={handleTypeCheck}
                                    purchase_type={"monthly_fee"}
                                    addTypeCheck
                                    selectedType={equipment?.type}
                                />
                            )}
                            {/* Show TPP option if:
                               1. Not a Paychex org, OR
                               2. No TPP equipment is selected yet, OR
                               3. This equipment is currently configured as TPP, OR
                               4. This is a TPP line item being modified (renderQuantity exists and equipment is in TPP list)
                               AND equipment must have apply_tpp flag enabled */}
                            {(org_id != PAYCHEX_ORG_ID ||
                                tpp_equipments_selected.length === 0 ||
                                equipment.type == "apply_tpp" ||
                                (typeof renderQuantity == "function" &&
                                    tpp_equipments_selected.includes(
                                        equipment.equipment_slug,
                                ))) &&
                                !!equipment.apply_tpp && (
                                    <ApplyTPP
                                        onInputChange={onInputChange}
                                        handleTypeCheck={handleTypeCheck}
                                        isChecked={equipment.type == "apply_tpp"}
                                        selectedType={equipment?.type}
                                    />
                            )}
                            {!!equipment.per_transaction && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Per Transaction"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.per_transaction}`}
                                    name={"per_transaction_default"}
                                    value={equipment.per_transaction_default}
                                    type="number"
                                    isChecked={equipment.type == "per_transaction"}
                                />
                            )}
                            {!!equipment.file_types && (
                                <FileTypeSelect
                                    label="File Types"
                                    onChange={onInputChange}
                                    placeholder="File Types"
                                    name={"file_type_key"}
                                    value={equipment.file_type_key}
                                    options={equipment.file_types_default}
                                />
                            )}
                            {!!equipment.hardware && (
                                <HardwareSelect
                                    label="Hardware"
                                    onChange={onInputChange}
                                    placeholder="Hardware"
                                    name={"hardware_id"}
                                    value={equipment.hardware_id}
                                    options={equipment.hardware_default}
                                    allEquipments={allEquipments}
                                />
                            )}
                            {!!equipment.other_equipments && (
                                <EquipmentSelect
                                    label="Equipment"
                                    onChange={onInputChange}
                                    placeholder="Equipment"
                                    name={"other_equipment_id"}
                                    value={equipment.other_equipment_id}
                                    options={equipment.other_equipments_default}
                                    allEquipments={allEquipments}
                                />
                            )}
                            {!!equipment.package && (
                                <PackageSelect
                                    id={equipment.id}
                                    label="Package"
                                    onChange={onInputChange}
                                    placeholder="Package"
                                    name={"package_key"}
                                    value={equipment.package_key}
                                    options={equipment.package_default}
                                    required={isValorEquipment}
                                />
                            )}
                            {!!equipment.amount && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Amount"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.amount}`}
                                    name={"amount_default"}
                                    value={equipment.amount_default}
                                    type="number"
                                />
                            )}
                            {!!equipment.factor_rate && (
                                <PercentageInput
                                    id={equipment.id}
                                    label="Factor Rate"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.factor_rate}%`}
                                    name={"factor_rate_default"}
                                    value={equipment.factor_rate_default}
                                    type="number"
                                />
                            )}
                            {!!equipment.payback && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Payback"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.payback}`}
                                    name={"payback_default"}
                                    value={equipment.payback_default}
                                    type="number"
                                />
                            )}
                            {!!equipment.monthly_wireless && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Monthly Wireless"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.monthly_wireless}`}
                                    name={"monthly_wireless_default"}
                                    value={equipment.monthly_wireless_default}
                                    type="number"
                                />
                            )}
                            {!!equipment.text_sms_fee && (
                                <CurrencyInput
                                    id={equipment.id}
                                    label="Text/SMS Fee"
                                    onChange={onInputChange}
                                    placeholder={`${equipment_default.text_sms_fee}`}
                                    name={"text_sms_fee_default"}
                                    value={equipment.text_sms_fee_default}
                                    type="number"
                                />
                            )}
                            {!!equipment.cable_length && (
                                <CableLength
                                    id={equipment.id}
                                    label="Length"
                                    onChange={onInputChange}
                                    placeholder="Length"
                                    name={"cable_length"}
                                    value={equipment.cable_length_key}
                                    options={equipment.cable_length_default}
                                />
                            )}
                            {!!equipment.customer_facing && (
                                <CustomerFacing
                                    id={equipment.id}
                                    onChange={handleInputChange}
                                    value={
                                        equipment.customer_facing_default || "no"
                                    }
                                />
                            )}
                            {!!equipment.virtual_terminal && (
                                <VirtualTerminal
                                    id={equipment.id}
                                    onChange={handleInputChange}
                                    value={
                                        equipment.virtual_terminal_default || "no"
                                    }
                                />
                            )}
                            {!!equipment.pin_debit && (
                                <PINDebit
                                    id={equipment.id}
                                    onChange={handleInputChange}
                                    value={equipment.pin_debit_default || "no"}
                                />
                            )}
                            {!!equipment.sim_card && (
                                <AddSIMCard
                                    onAddSIM={handleInputChange}
                                    onInputChange={onInputChange}
                                    value={equipment.sim_card_monthly_default}
                                    addSim={equipment.sim_card_default || "no"}
                                    placeholder={equipment_default.sim_card_monthly}
                                />
                            )}
                            {!!equipment.pin_pad && (
                                <PinPadInput
                                    isChecked={equipment.pin_pad_selected}
                                    onInputChange={onInputChange}
                                    pinPadName={equipment?.pin_pad_name_default}
                                />
                            )}
                            {!!equipment.connectivity && (
                                <Connectivity
                                    id={equipment.id}
                                    onChange={handleInputChange}
                                    value={equipment.connectivity_default}
                                    options={equipment.connectivity_options}
                                />
                            )}
                            {equipment.connectivity_default &&
                                equipment.connectivity_default == "sim" && (
                                    <>
                                        <SimProvidedBy
                                            name="sim_provided_by"
                                            label="SIM Provided By"
                                            onChange={onInputChange}
                                            value={equipment.sim_provided_by}
                                        />
                                        {equipment.sim_provided_by ==
                                            "processor_provided" && (
                                            <>
                                                {!!equipment.monthly_cellular_txn && (
                                                    <CurrencyInput
                                                        label="Monthly Cell Txn"
                                                        placeholder={"25.00"}
                                                        value={
                                                            equipment.monthly_cellular_txn_default
                                                        }
                                                        name={
                                                            "monthly_cellular_txn_default"
                                                        }
                                                        type="number"
                                                        onChange={onInputChange}
                                                    />
                                                )}
                                                {!!equipment.cellular_per_txn && (
                                                    <CurrencyInput
                                                        label="Cellular/Txn"
                                                        endAdornment="/txn"
                                                        placeholder={"0.10"}
                                                        value={
                                                            equipment.cellular_per_txn_default
                                                        }
                                                        name={
                                                            "cellular_per_txn_default"
                                                        }
                                                        type="number"
                                                        onChange={onInputChange}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            {!!equipment.add_on_equipment &&
                                !(equipment.id === 19 && org_id === EMS_ORG_ID) && (
                                    <AddonSelect
                                        addon_list={equipment.add_on_equipment_list}
                                        handleOnChange={handleAddonChange}
                                        selectedAddons={equipment.selected_addons || []}
                                        id={equipment.id}
                                    />
                                )}
                        </>
                    </div>
                )}
            </div>
            <div className="action-btn-section">
                {typeof renderActionBtn === "function" && renderActionBtn()}
            </div>
        </div>
    );
};

export default Equipment;
